import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import config from "../../config";
import api from "../../services/api";
import NoDataFound from '../../shared/NoDataFound';
import { useTableState } from "./utils/useTableState";
import { usePersistedFilterState } from "./utils/usePersistedFilterState";
import { alphaformatDateTime } from "../../utils/dateFormat";
import TableLoader from "../../shared/TableLoader";
import FilterBar from './components/FilterBar';
import ContentDrawer from "./components/ContentDrawer";
import LocationDetails from "./components/LocationDetails";
import { renderSalesPersonCell } from "./utils/cellRenderers";
import CreativeName from "./components/CreativeName";
import { useUnreadCountPolling } from "./utils/useUnreadCountPolling";
import { useVisibleRowIds } from "./utils/useVisibleRowIds";

 const types = [
  {
    value: "all",
    label: "All",
  },
  {
    value: "full_page",
    label: "Full Image",
  },
  {
    value: "full_video",
    label: "Full Video",
  },
  {
      value:"half_video",
      label:"Half Video"
  },
  {
    value: "promoted_image_story",
    label: "Promoted Image Story ",
  },
  {
    value:"promoted_video_story",
    label:"Promoted Video Story"
  },
  {
    value: "classifiedads",
    label: "Classified Ads"
  }
]
const STATUS_OPTIONS = [
  { value:-1, label: 'Pending Request'},
  { value: 2, label: 'Saved' },
  { value: 3, label: 'Picked' },
  { value: 4, label: 'Completed' },
  { value: 5, label: 'Rejected' },
  { value: 6, label: 'Accepted' },
];

const ReadyToLiveAds = () => {
  const [readyAds, setReadyAds] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loader, setloader] = useState(false);
  const { currentPage, setCurrentPage, salesList, getSalesList, fetchSalesLocations, salesLocations} = useTableState();
  const { visibleIds, rowRef } = useVisibleRowIds();
  const liveUnread = useUnreadCountPolling(readyAds, "/alpha_cst_designer/getUnreadCounts", { visibleIds });
  const [designers, setDesigners] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const sentinelRef = useRef(null);
  const mobileSentinelRef = useRef(null);
  const loadMoreCtxRef = useRef({});
  const [showAdDetails, setShowAdDetails] = useState(false);
  const [rowData, setRowData] = useState({});
  // Filter state
  // const today = new Date().toISOString().split('T')[0];
  // const tomorrow = new Date();
  // tomorrow.setDate(tomorrow.getDate() + 1);
  // const tomorrowStr = tomorrow.toISOString().split('T')[0];
  const [dateRange, setDateRange] = usePersistedFilterState('alpha_designer.unpicked_ads.ready_to_live.dateRange', { startDate: null, endDate: null });
  const [designer, setDesigner] = useState(null);
  const [status, setStatus] = useState(null);
  const [adType, setAdType] = usePersistedFilterState('alpha_designer.unpicked_ads.ready_to_live.adType', null);
  const [search, setSearch] = usePersistedFilterState('alpha_designer.unpicked_ads.ready_to_live.search', '');
  const [salesPerson, setSalesPerson] = usePersistedFilterState('alpha_designer.unpicked_ads.ready_to_live.salesPerson', null);
  const [salesLocation, setSalesLocation] = usePersistedFilterState('alpha_designer.unpicked_ads.ready_to_live.salesLocation', []);

  // Designer options for react-select
  const designerOptions = useMemo(() =>
    designers.map(d => ({ value: d.designer_id, label: d.name })),
    [designers]
  );

  // Fetch designers on mount
  useEffect(() => {
    const fetchDesigners = async () => {
      try {
        let res = await api({
          url: `/alpha_cst_designer/getDesigners`,
          method: "POST",
          responseType: "json",
          data: {}
        });
        if (res.code === 200)
          setDesigners(res.data?.data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchDesigners();
    getSalesList();
    fetchSalesLocations();
    setCurrentPage(0);
  }, []);

  const FetchReadyToLive = async (page, append = false) => {
    try {
      setloader(true);
      let res = await api({
        url: `/alpha_cst_designer/getReadytoLiveAds`,
        method: "POST",
        responseType: "json",
        data: {
          page,
          limit: config.PER_PAGE,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          designerId: designer ? designer.value : null,
          status: status ? status.value : null,
          adType: adType && adType.value !== 'all' ? adType.value : null,
          search: search || null,
          sales_person_id: salesPerson?.value ? [salesPerson.value] : null,
          sales_location: salesLocation?.length ? salesLocation.map((o) => o.value) : null,
        }
      });
      if (res.code === 200) {
        const newData = res.data?.data || [];
        const total = res.data?.total || 0;
        if (append) {
          setReadyAds(prev => [...prev, ...newData]);
        } else {
          setReadyAds(newData);
        }
        const totalPgs = Math.ceil(total / config.PER_PAGE);
        setHasMore((page + 1) < totalPgs);
        setTotalRecords(total);
      }
      setloader(false);
    } catch (error) {
      setloader(false);
      console.error(error);
    }
  }

  // Update ref for load more
  loadMoreCtxRef.current = { loader, hasMore, currentPage };

  // Reset and fetch on filter change
  useEffect(() => {
    setCurrentPage(0);
    setReadyAds([]);
    FetchReadyToLive(0, false);
  }, [dateRange, designer, status, adType, search, salesPerson, salesLocation]);

  const handleLoadMore = useCallback(() => {
    const ctx = loadMoreCtxRef.current;
    if (ctx.loader || !ctx.hasMore) return;
    const nextPage = ctx.currentPage + 1;
    setCurrentPage(nextPage);
    FetchReadyToLive(nextPage, true);
  }, []);

  useEffect(() => {
    const sentinels = [sentinelRef.current, mobileSentinelRef.current].filter(Boolean);
    if (!sentinels.length) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) handleLoadMore(); },
      { threshold: 0.1 }
    );
    sentinels.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [hasMore, handleLoadMore, readyAds.length]);

  const checkpayment = async (creative_id, latest_design, status) => {
    try {
      let res = await api({
        url: `/alpha_cst_designer/pickcompletedAd`,
        method: "POST",
        responseType: "json",
        data: {
          creative_id,
          latest_design,
          status
        }
      });
      if (res.code === 200) {
        setCurrentPage(0);
        setReadyAds([]);
        FetchReadyToLive(0, false);
      }
    }
    catch (error) {
      console.error(error);
    }
  }

  // Handlers
  const handleDateChange = (val) => setDateRange(val);
  const handleDesignerChangeSelect = (val) => setDesigner(val);
  const handleStatusChange = (val) => setStatus(val);
  const handleAdTypeChange = (val) => setAdType(val);
  const handleSearchChange = (e) => setSearch(e.target.value);
  const handleSalesPersonChange = (val) => setSalesPerson(val);
  const handleSalesLocationChange = (val) => setSalesLocation(val);

  return (
    <div>
      <FilterBar
        dateRange={dateRange}
        onDateChange={handleDateChange}
        designer={designer}
        onDesignerChange={handleDesignerChangeSelect}
        designerOptions={designerOptions}
        status={status}
        onStatusChange={handleStatusChange}
        statusOptions={STATUS_OPTIONS}
        showDesigner={false}
        showStatus={false}
        adType={adType}
        onAdTypeChange={handleAdTypeChange}
        adTypeOptions={types}
        showAdType={true}
        search={search}
        onSearchChange={(val) => setSearch(val)}
        showSearch={true}
        salesPerson={salesPerson}
        onSalesPersonChange={handleSalesPersonChange}
        salesPersonOptions={salesList}
        showSalesPerson={true}
        salesLocation={salesLocation}
        onSalesLocationChange={handleSalesLocationChange}
        showSalesLocation={true}
        salesLocationOptions={salesLocations}
      />
       <div className='flex flex-col items-start justify-center gap-2'>
          <div className="px-2 py-1 shadow-md rounded-lg bg-gray-300">
              <span>Total Ads: {totalRecords}</span>
          </div>

          {/* ---- Mobile-only card view (<=767px) ---- */}
          <div className="lineitems-mobile-view w-full">
            {loader && readyAds.length === 0 ? (
              <div className="text-center text-gray-500 py-6 italic">Loading...</div>
            ) : readyAds.length > 0 ? (
              readyAds.map((rawRow) => {
                const liveCount = liveUnread[rawRow.creative_id];
                const row = liveCount != null ? { ...rawRow, unread_count: liveCount, unreadCount: liveCount } : rawRow;
                const created_at = alphaformatDateTime(row.created_at);
                return (
                  <div key={row.creative_id} ref={rowRef(rawRow.creative_id, 'mobile')} className="ads-card">
                    <div className="ads-card__head">
                      <div className="ads-card__title"><CreativeName row={row} /></div>
                    </div>
                    <div className="ads-card__body">
                      <div className="ads-row"><span className="ads-row__label">Sales Person</span><div className="ads-row__val">{renderSalesPersonCell(row.sales_person, row)}</div></div>
                      <div className="ads-row"><span className="ads-row__label">Created</span><div className="ads-row__val">{created_at.dateStr + ' ' + created_at.timeStr}</div></div>
                      <div className="ads-row"><span className="ads-row__label">Ad Creative</span><div className="ads-row__val">
                        <div className="flex flex-col gap-1 items-start">
                          {row.type && <span>{row.type}</span>}
                          {row.ad_creative_sub_type && <span>{row.ad_creative_sub_type}</span>}
                        </div>
                      </div></div>
                      <div className="ads-row"><span className="ads-row__label">Location</span><div className="ads-row__val"><LocationDetails data={row} /></div></div>
                    </div>
                    <div className="ads-card__foot">
                      <button className="flex-1 bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700" onClick={() => checkpayment(row.creative_id, row.latest_design, row.status)}>Pick Ad</button>
                      <button className="flex-1 bg-gray-600 text-white px-3 py-2 rounded hover:bg-gray-700" onClick={() => { setRowData(row); setShowAdDetails(true); }}>View Details</button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center text-gray-500 py-6 italic">No data found.</div>
            )}
            {hasMore && !loader && <div ref={mobileSentinelRef} className="h-6 w-full" />}
          </div>

          <div className="mobHide w-full">
          <div className="overflow-auto">
          <table className="table table-bordered">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 text-left">Sales Person Details</th>
                <th>Ad Creation Date</th>
                <th>Campaign Name</th>
                <th>Ad Creative</th>
                <th>Location Details</th>
                <th>Action</th>
                <th>Ad Details</th>
              </tr>
            </thead>
            {loader && readyAds.length === 0 ? (
              <TableLoader colspan="15" />
            ) : readyAds.length > 0 ? (
              readyAds.map((rawRow, idx) => {
                const liveCount = liveUnread[rawRow.creative_id];
                const row = liveCount != null ? { ...rawRow, unread_count: liveCount, unreadCount: liveCount } : rawRow;
                const created_at = alphaformatDateTime(row.created_at);
                const start_date = alphaformatDateTime(row.creative_start_date);
                const end_date = alphaformatDateTime(row.creative_start_date,'end');
                return (
                  <tbody key={row.creative_id} ref={rowRef(rawRow.creative_id, 'desktop')}>
                    <tr className={`border-t ${idx %2 === 0 ? '' : 'bg-gray-100'}`}>
                      <td>
                          {renderSalesPersonCell(row.sales_person,row)}
                        </td>

                      <td>
                        <div className="flex items-start flex-col">
                          <p>created on : </p>
                          <div>{created_at.dateStr+' '+created_at.timeStr}</div>
                        </div>
                      </td>
                      <td>
                        <CreativeName row={row}/>
                      </td>
                      <td>
                        <div className="flex flex-col gap-1 justify-start items-start">
                          {row.type && <><span>{row.type}</span> <hr className="w-full border-gray-500 my-1" /></>}
                          {row.ad_creative_sub_type && <span>{row.ad_creative_sub_type}</span>}
                        </div>
                      </td>
                      <td>
                        <LocationDetails data={row}/>
                      </td>
                      <td>
                        <button
                          className="bg-blue-600 text-white px-3 py-1 rounded"
                          onClick={() => {
                            checkpayment(row.creative_id, row.latest_design, row.status);
                          }}
                        >
                          Pick Ad
                        </button>
                      </td>
                      <td>
                        <button className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700" onClick={()=>{setRowData(row); setShowAdDetails(true)}}>View Ad Details</button>
                      </td>
                    </tr>
                  </tbody>
                );
              })
            ) : (
              <NoDataFound colspan="15" />
            )}
          </table>
        </div>
        </div>
      </div>
      {hasMore && !loader && <div ref={sentinelRef} className="h-6 w-full" />}
      {loader && readyAds.length > 0 && <div className="flex justify-center py-3"><div className="spinner-border text-primary" role="status" /></div>}
      <ContentDrawer isOpen={showAdDetails} type="adDetails" rowData={rowData} onClose={() => setShowAdDetails(!showAdDetails)} />
    </div>
  );
};

export default ReadyToLiveAds;
